import type { Vec2 } from '@/geometry/vec2';
import { dist2, sub2, heading2, len2 } from '@/geometry/vec2';
import { closestPointOnSegment, lineLineIntersection } from '@/geometry/intersect';
import type { CreasePattern } from '@/origami/types';
import type { SnapSettings } from '@/state/store';
import { SNAP_RADIUS_PX, toRadians } from '@/constants/numeric';
import { pxToWorld, type Viewport } from './viewport';

export type SnapKind =
  | 'vertex'
  | 'intersection'
  | 'midpoint'
  | 'edge'
  | 'grid'
  | 'axis'
  | 'angle'
  | 'perpendicular'
  | 'free';

export interface SnapGuide {
  kind: 'line' | 'point' | 'circle';
  a: Vec2;
  b?: Vec2;
}

export interface SnapResult {
  point: Vec2;
  kind: SnapKind;
  label: string;
  guides: SnapGuide[];
  /** Vertex index when the snap landed exactly on an existing vertex. */
  vertex?: number;
}

/**
 * CAD-style snapping.
 *
 * Candidates are gathered in priority order and the first one inside the screen
 * space radius wins, so a vertex always beats the grid underneath it. Everything
 * is compared in *screen* pixels, which is what makes the feel consistent at any
 * zoom level - comparing in world units would make snapping unusable when zoomed
 * out.
 */
export function computeSnap(
  world: Vec2,
  pattern: CreasePattern,
  settings: SnapSettings,
  vp: Viewport,
  anchor: Vec2 | null,
): SnapResult {
  const free: SnapResult = { point: world, kind: 'free', label: '', guides: [] };
  if (!settings.enabled) return free;

  const radius = pxToWorld(vp, SNAP_RADIUS_PX);
  const coords = pattern.verticesCoords;

  // 1. Existing vertices.
  if (settings.vertices) {
    let best = -1;
    let bestD = radius;
    for (let v = 0; v < coords.length; v++) {
      const d = dist2(coords[v], world);
      if (d < bestD) {
        bestD = d;
        best = v;
      }
    }
    if (best >= 0) {
      return {
        point: coords[best],
        kind: 'vertex',
        label: `vertex ${best}`,
        guides: [{ kind: 'point', a: coords[best] }],
        vertex: best,
      };
    }
  }

  // 2. Crossings of nearby edges (real intersections, not just proximity).
  if (settings.intersections) {
    const near = nearbyEdges(pattern, world, radius * 6);
    for (let i = 0; i < near.length; i++) {
      for (let j = i + 1; j < near.length; j++) {
        const [a1, b1] = pattern.edgesVertices[near[i]];
        const [a2, b2] = pattern.edgesVertices[near[j]];
        const hit = lineLineIntersection(
          coords[a1],
          sub2(coords[b1], coords[a1]),
          coords[a2],
          sub2(coords[b2], coords[a2]),
        );
        if (!hit) continue;
        if (dist2(hit, world) > radius) continue;
        if (!onSegment(hit, coords[a1], coords[b1]) || !onSegment(hit, coords[a2], coords[b2])) {
          continue;
        }
        return {
          point: hit,
          kind: 'intersection',
          label: 'intersection',
          guides: [{ kind: 'point', a: hit }],
        };
      }
    }
  }

  // 3. Edge midpoints.
  if (settings.midpoints) {
    const near = nearbyEdges(pattern, world, radius * 4);
    for (const e of near) {
      const [a, b] = pattern.edgesVertices[e];
      const mid = { x: (coords[a].x + coords[b].x) / 2, y: (coords[a].y + coords[b].y) / 2 };
      if (dist2(mid, world) <= radius) {
        return {
          point: mid,
          kind: 'midpoint',
          label: `midpoint of edge ${e}`,
          guides: [{ kind: 'point', a: mid }],
        };
      }
    }
  }

  // 4. Anchor-relative constraints: axis lock, angle increments, perpendicular.
  if (anchor) {
    const d = sub2(world, anchor);
    const length = len2(d);
    if (length > 1e-9) {
      const angle = heading2(d);
      if (settings.angles && settings.angleStepDegrees > 0) {
        const step = toRadians(settings.angleStepDegrees);
        const snapped = Math.round(angle / step) * step;
        const delta = Math.abs(normalize(angle - snapped));
        if (delta * length < radius * 1.5) {
          const point = {
            x: anchor.x + Math.cos(snapped) * length,
            y: anchor.y + Math.sin(snapped) * length,
          };
          const isAxis = Math.abs(Math.sin(snapped)) < 1e-9 || Math.abs(Math.cos(snapped)) < 1e-9;
          return {
            point,
            kind: isAxis && settings.axes ? 'axis' : 'angle',
            label: `${((snapped * 180) / Math.PI).toFixed(1)}°`,
            guides: [{ kind: 'line', a: anchor, b: point }],
          };
        }
      }
    }
  }

  // 5. Projection onto an edge.
  if (settings.edges) {
    let bestPoint: Vec2 | null = null;
    let bestD = radius;
    let bestEdge = -1;
    for (const e of nearbyEdges(pattern, world, radius * 4)) {
      const [a, b] = pattern.edgesVertices[e];
      const r = closestPointOnSegment(world, coords[a], coords[b]);
      if (r.distance < bestD) {
        bestD = r.distance;
        bestPoint = r.point;
        bestEdge = e;
      }
    }
    if (bestPoint) {
      return {
        point: bestPoint,
        kind: 'edge',
        label: `on edge ${bestEdge}`,
        guides: [{ kind: 'point', a: bestPoint }],
      };
    }
  }

  // 6. Grid.
  if (settings.grid && settings.gridDivisions > 0) {
    const extent = patternExtent(pattern);
    const cell = extent / settings.gridDivisions;
    if (cell > 0) {
      const gx = Math.round(world.x / cell) * cell;
      const gy = Math.round(world.y / cell) * cell;
      const candidate = { x: gx, y: gy };
      if (dist2(candidate, world) <= radius) {
        return {
          point: candidate,
          kind: 'grid',
          label: 'grid',
          guides: [{ kind: 'point', a: candidate }],
        };
      }
    }
  }

  return free;
}

function normalize(a: number): number {
  let x = (a + Math.PI) % (2 * Math.PI);
  if (x < 0) x += 2 * Math.PI;
  return x - Math.PI;
}

function onSegment(p: Vec2, a: Vec2, b: Vec2): boolean {
  const r = closestPointOnSegment(p, a, b);
  return r.distance < 1e-9;
}

function nearbyEdges(pattern: CreasePattern, p: Vec2, radius: number): number[] {
  const out: number[] = [];
  const coords = pattern.verticesCoords;
  for (let e = 0; e < pattern.edgesVertices.length; e++) {
    const [a, b] = pattern.edgesVertices[e];
    const r = closestPointOnSegment(p, coords[a], coords[b]);
    if (r.distance <= radius) out.push(e);
  }
  return out;
}

export function patternExtent(pattern: CreasePattern): number {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const v of pattern.verticesCoords) {
    minX = Math.min(minX, v.x);
    maxX = Math.max(maxX, v.x);
    minY = Math.min(minY, v.y);
    maxY = Math.max(maxY, v.y);
  }
  if (!Number.isFinite(minX)) return 1;
  return Math.max(maxX - minX, maxY - minY, 1e-6);
}
